import React, { useEffect, useState } from "react";
import { getTickets, getSupportAgents } from "../services/ticket.service";
import { IonPage, IonHeader, IonToolbar, IonButtons, IonMenuButton, IonTitle, IonContent, IonRefresher, IonRefresherContent, IonList, IonItem, IonLabel, IonText, IonSpinner, IonRow, IonBackButton } from "@ionic/react";
import { chevronDownCircleOutline } from "ionicons/icons";
import { doReload } from "../hooks/common";

const TicketsOverview: React.FC = () => {
  const [fetchingAgents, setFetchingAgents] = useState<boolean>(false);
  const [fetchingTickets, setFetchingTickets] = useState<boolean>(false);
  const [totalTickets, setTotalTickets] = useState<number>(0);

  const [ownerTicketCount, setOwnerTicketCount] = useState<Record<string, number>>();
  const [ownerDetail, setOwnerDetail] = useState<Record<string, any>>();

  useEffect(() => {
    fetchAgents();
    fetchTickets();
  }, [])

  const fetchAgents = async () => {
    setFetchingAgents(true);
    getSupportAgents()
      .then((response) => {
        const detail: Record<string, any> = {};
        response.forEach(agent => { detail[agent.farmUserId] = agent; });
        setOwnerDetail(detail);
      })
      .catch(console.error)
      .finally(() => setFetchingAgents(false));
  }

  const fetchTickets = () => {
    setFetchingTickets(true);
    getTickets({ status: 'open' }, 1000, 0)
      .then(({ tickets, total }) => {
        const grouped: Record<string, number> = {};
        tickets.forEach(t => {
          if (t.assigneeId) {
            grouped[t.assigneeId] = (grouped[t.assigneeId] ?? 0) + 1;
          }
        });
        setOwnerTicketCount(grouped);
        setTotalTickets(total);
      })
      .catch(console.error)
      .finally(() => setFetchingTickets(false));
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton />
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Ticket Overview</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={doReload}>
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            pullingText="Pull to refresh"
            refreshingSpinner="circles"
            refreshingText="Refreshing...">
          </IonRefresherContent>
        </IonRefresher>
        <UserTicketInfo
          ownerDetail={ownerDetail}
          ownerTicketCount={ownerTicketCount}
          fetchingTickets={fetchingTickets}
          fetchingAgents={fetchingAgents}
          totalTickets={totalTickets}
        />
      </IonContent>
    </IonPage>
  )
}

export default TicketsOverview;

type UserTicketInfoProps = {
  ownerDetail?: Record<string, any>,
  ownerTicketCount?: Record<string, number>,
  fetchingTickets: boolean;
  fetchingAgents: boolean;
  totalTickets: number;
}
const UserTicketInfo: React.FC<UserTicketInfoProps> = (props) => {
  const { ownerDetail, ownerTicketCount, fetchingTickets, fetchingAgents, totalTickets } = props;

  if (fetchingAgents || fetchingTickets) {
    return (
      <IonRow>
        <IonSpinner style={{ margin: '10px auto' }} />
      </IonRow>
    );
  }

  const sortedEntries = ownerTicketCount
    ? Object.entries(ownerTicketCount).sort(([, a], [, b]) => b - a)
    : [];

  return (
    <IonList>
      <IonItem>
        <IonLabel style={{ fontWeight: '600' }}>Open Tickets</IonLabel>
        <IonText style={{ fontWeight: '600' }}>{totalTickets}</IonText>
      </IonItem>
      {sortedEntries.map(([assigneeId, ticketCount]) => {
        const details = ownerDetail?.[assigneeId];
        const ownerName = details?.name ?? assigneeId;

        return (
          <IonItem key={assigneeId}>
            <IonLabel>{ownerName}</IonLabel>
            <IonText>{ticketCount}</IonText>
          </IonItem>
        );
      })}
    </IonList>
  );
};